import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Download, CheckCircle, AlertCircle, Book, User, Calendar, CreditCard } from 'lucide-react';
import { purchaseService, downloadService } from '../services/newApi';
import { toast } from 'sonner';

const DownloadPage = () => {
  const [searchParams] = useSearchParams();
  const purchaseId = searchParams.get('purchase_id');
  
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingBooks, setDownloadingBooks] = useState({});

  useEffect(() => {
    if (purchaseId) {
      fetchPurchaseDetails();
    } else {
      setLoading(false);
    }
  }, [purchaseId]);

  const fetchPurchaseDetails = async () => {
    try {
      setLoading(true);
      const response = await purchaseService.getPurchaseDetails(purchaseId);
      
      if (response.success) {
        setPurchaseDetails(response.data);
      } else {
        throw new Error('Failed to fetch purchase details');
      }
    } catch (error) {
      toast.error('Failed to load purchase details', {
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (bookId, bookTitle) => {
    setDownloadingBooks(prev => ({ ...prev, [bookId]: true }));
    
    try {
      const result = await downloadService.downloadPDF(purchaseId, bookId);
      
      if (result.success) {
        toast.success(`${bookTitle} downloaded successfully! 📚`, {
          description: `File: ${result.filename}`,
          duration: 5000,
        });
        
        // Refresh purchase details to update download counts
        await fetchPurchaseDetails();
      }
    } catch (error) {
      toast.error(`Failed to download ${bookTitle}`, {
        description: error.message,
      });
    } finally {
      setDownloadingBooks(prev => ({ ...prev, [bookId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your purchase details...</p>
        </div>
      </div>
    );
  }

  if (!purchaseId || !purchaseDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="text-center p-8">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Access</h2>
            <p className="text-gray-600">
              This page requires a valid purchase ID. Please complete your purchase first.
            </p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="mt-4 bg-blue-600 hover:bg-blue-700"
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h1>
          <p className="text-xl text-gray-600">
            Your Marriage 101 books are ready for download
          </p>
        </div>

        {/* Purchase Summary */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CreditCard className="w-5 h-5 mr-2" />
              Purchase Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Customer</p>
                  <p className="font-semibold">{purchaseDetails.customer_name}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Purchase Date</p>
                  <p className="font-semibold">
                    {new Date(purchaseDetails.purchase_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <CreditCard className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Amount Paid</p>
                  <p className="font-semibold">₹{purchaseDetails.amount_paid.toLocaleString()}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-blue-800 font-medium">
                Order ID: {purchaseDetails.purchase_id}
              </p>
              <p className="text-blue-700 text-sm mt-1">
                Purchase Type: {purchaseDetails.purchase_type === 'bundle' ? 'Complete Series' : 'Individual Book'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Books Download Section */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Book className="w-6 h-6 mr-2" />
            Your Books
          </h2>
          
          <div className="grid gap-6">
            {purchaseDetails.books.map((book) => (
              <Card key={book.id} className="overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0">
                    
                    {/* Book Info */}
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {book.title}
                      </h3>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span>
                          Downloads used: {book.downloads_used} / {book.downloads_used + book.downloads_remaining}
                        </span>
                        {book.downloads_remaining > 0 && (
                          <span className="text-green-600 font-medium">
                            {book.downloads_remaining} remaining
                          </span>
                        )}
                      </div>
                      
                      {book.downloads_remaining === 0 && (
                        <p className="text-amber-600 text-sm mt-2">
                          ⚠️ Download limit reached. Contact support if you need additional downloads.
                        </p>
                      )}
                    </div>

                    {/* Download Button */}
                    <div className="flex-shrink-0">
                      <Button
                        onClick={() => handleDownload(book.id, book.title)}
                        disabled={!book.can_download || downloadingBooks[book.id]}
                        className={`px-6 py-3 ${
                          book.can_download 
                            ? 'bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800' 
                            : 'bg-gray-300 cursor-not-allowed'
                        } text-white font-semibold rounded-lg transition-all duration-300`}
                      >
                        {downloadingBooks[book.id] ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Preparing...
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <Download className="w-4 h-4 mr-2" />
                            {book.can_download ? 'Download PDF' : 'Limit Reached'}
                          </div>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Important Notes */}
        <Card className="mt-8 border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-amber-800 mb-3">Important Information</h3>
            <div className="space-y-2 text-amber-700 text-sm">
              <p>• Each PDF is watermarked with your name and mobile number for security</p>
              <p>• You can download each book up to 3 times</p>
              <p>• Sharing or unauthorized distribution is prohibited</p>
              <p>• Keep this page bookmarked for future downloads</p>
              <p>• Contact support at +91 8977923805 if you face any issues</p>
            </div>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Button 
            onClick={() => window.location.href = '/'}
            variant="outline"
            className="px-6 py-3"
          >
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;